import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";

export type TFormConfig = {
  id: number;
  updatedAt: string;
  serviceName: string;
  active?: boolean;
};

export type TFormConfigWithSteps = TFormConfig & {
  steps?: { type: string; id: number }[];
};

export type TCreateFormConfigPaylod = {
  serviceName: string;
  active?: boolean;
  forms: number[];
};

export type TPatchFormConfigPayload = TCreateFormConfigPaylod & { id: number };

export const formConfigKeyStore = {
  createdFormConfigs: () => ["created-form-configs"],
  formConfigsCount: (...args: (number | string)[]) => [
    "form-configs-count",
    ...args,
  ],
  formConfigs: (...args: (number | string)[]) => ["form-configs", ...args],
  formConfig: (id?: number) => ["form-config", id],
};

export function useFormConfigs({
  limit,
  page,
  search,
  sort,
  sortVal,
}: {
  page: number;
  limit: number;
  search?: string;
  sort?: string;
  sortVal?: number;
}) {
  return useQuery({
    queryFn: () =>
      _axios.get<{ success: true; data: Array<TFormConfig> }>(
        "/forms/configs",
        { params: { limit, page, search, sort, sortVal } }
      ),
    queryKey: formConfigKeyStore.formConfigs(
      page,
      limit,
      search || "",
      sort || "",
      sortVal || 1
    ),
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (result) => result.data,
  });
}

export function useFormConfigCount({ search }: { search?: string }) {
  return useQuery({
    queryFn: () =>
      _axios.get<{ success: true; data: number }>("/forms/configs/count", {
        params: { search },
      }),
    queryKey: formConfigKeyStore.formConfigsCount(search || ""),
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (result) => result.data,
  });
}

export function useCreateFormConfig() {
  const qc = useQueryClient();

  return useMutation<
    { success: boolean },
    AxiosError | Error,
    TCreateFormConfigPaylod
  >({
    mutationFn: (data) =>
      _axios
        .post<{ success: boolean }>(`/forms/configs`, data)
        .then((res) => res.data),
    onError: () => {
      ErrorToast("Something went wrong while creating form config!");
    },
    onSuccess: () => {
      SuccessToast("Form added successfully!");
      qc.invalidateQueries({ queryKey: formConfigKeyStore.formConfigs() });
      qc.invalidateQueries({ queryKey: formConfigKeyStore.formConfigsCount() });
      return qc.invalidateQueries({
        queryKey: formConfigKeyStore.createdFormConfigs(),
      });
    },
  });
}

export function useAlreadyCreatedFormConfigs() {
  return useQuery({
    queryKey: formConfigKeyStore.createdFormConfigs(),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: string[];
      }>("/forms/configs/created"),
    select: (result) => result.data?.data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGetFormConfig(id?: number) {
  return useQuery({
    enabled: !!id,
    queryKey: formConfigKeyStore.formConfig(id),
    queryFn: () =>
      _axios.get<{ success: true; data: TFormConfigWithSteps }>(
        `/forms/configs/${id}`
      ),
    select: (result) => result.data?.data,
  });
}

export function usePatchFormConfig() {
  const qc = useQueryClient();

  return useMutation<
    { success: boolean },
    AxiosError | Error,
    TPatchFormConfigPayload
  >({
    mutationFn: (data) =>
      _axios
        .patch<{ success: boolean }>(`/forms/configs/${data.id}`, data)
        .then((res) => res.data),
    onError: () => {
      ErrorToast("Something went wrong while updating form config!");
    },
    onSuccess: (_, { id }) => {
      SuccessToast("Form config updated successfully!");
      qc.invalidateQueries({ queryKey: formConfigKeyStore.formConfigs() });
    },
  });
}
